import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Bell, Send, Smartphone, Users, Building2, Globe, Link } from 'lucide-react';

const TARGET_OPTIONS = [
  { value: 'all', label: '전체 디바이스', icon: Globe, desc: '앱을 설치한 모든 사용자' },
  { value: 'member', label: '일반회원', icon: Users, desc: '가입된 일반회원 디바이스' },
  { value: 'client', label: '권리사', icon: Building2, desc: '로그인된 권리사 디바이스' },
];

export default function AdminPushNotificationPage() {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [url, setUrl] = useState('');
  const [target, setTarget] = useState('all');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [stats, setStats] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get('/api/push/stats');
      setStats(res.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleSend = async () => {
    if (!title.trim() || !body.trim()) return alert('제목과 내용을 입력해주세요.');
    const t = TARGET_OPTIONS.find((o) => o.value === target);
    if (!window.confirm(`${t.label}에게 푸시 알림을 발송하시겠습니까?`)) return;

    setSending(true);
    setResult(null);
    try {
      const res = await axios.post('/api/push/send', {
        title: title.trim(),
        body: body.trim(),
        target,
        url: url.trim() || undefined,
      });
      setResult(res.data);
      setTitle('');
      setBody('');
      setUrl('');
    } catch (err) {
      alert(err.response?.data?.errorMessage || '발송에 실패했습니다.');
    }
    setSending(false);
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      {/* 헤더 */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-sky-500/15 flex items-center justify-center">
            <Bell className="w-5 h-5 text-sky-400" />
          </div>
          <div>
            <h1 className="text-white text-xl font-bold">푸시 알림 발송</h1>
            <p className="text-slate-500 text-sm">앱 사용자에게 푸시 알림을 전송합니다</p>
          </div>
        </div>
      </div>

      {/* 디바이스 통계 */}
      {stats && (
        <div className="grid grid-cols-3 gap-3 mb-6">
          {[
            { label: '전체', value: stats.all, icon: Smartphone, color: 'sky' },
            { label: '일반회원', value: stats.members, icon: Users, color: 'emerald' },
            { label: '권리사', value: stats.clients, icon: Building2, color: 'rose' },
          ].map((s) => (
            <div key={s.label} className="bg-[#182334] rounded-xl p-4 border border-white/5 text-center">
              <p className="text-slate-500 text-xs mb-1">{s.label}</p>
              <p className="text-white text-2xl font-bold">{s.value.toLocaleString()}</p>
              <p className="text-slate-600 text-xs mt-0.5">디바이스</p>
            </div>
          ))}
        </div>
      )}

      {/* 발송 폼 */}
      <div className="bg-[#182334] rounded-2xl border border-white/5 p-6 space-y-5">

        {/* 수신 대상 */}
        <div>
          <label className="block text-slate-400 text-sm font-medium mb-2">수신 대상</label>
          <div className="grid grid-cols-3 gap-2">
            {TARGET_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setTarget(opt.value)}
                className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border text-sm font-medium transition-all
                  ${target === opt.value
                    ? 'border-sky-500 bg-sky-500/10 text-sky-400'
                    : 'border-white/5 bg-white/3 text-slate-400 hover:border-white/15'}`}>
                <opt.icon className="w-5 h-5" />
                <span className="text-xs">{opt.label}</span>
              </button>
            ))}
          </div>
          <p className="text-slate-600 text-xs mt-2">
            {TARGET_OPTIONS.find((o) => o.value === target)?.desc}
          </p>
        </div>

        {/* 제목 */}
        <div>
          <label className="block text-slate-400 text-sm font-medium mb-2">
            알림 제목 <span className="text-rose-400">*</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={50}
            placeholder="예: 이번 주 플레이리스트가 업데이트됐습니다"
            className="w-full bg-[#0d1526] text-white rounded-xl px-4 py-3 text-sm border border-white/5 focus:outline-none focus:border-sky-500/50 placeholder:text-slate-600"
          />
          <p className="text-slate-600 text-xs mt-1 text-right">{title.length}/50</p>
        </div>

        {/* 내용 */}
        <div>
          <label className="block text-slate-400 text-sm font-medium mb-2">
            알림 내용 <span className="text-rose-400">*</span>
          </label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={200}
            rows={3}
            placeholder="예: 지금 앱에서 확인해보세요"
            className="w-full bg-[#0d1526] text-white rounded-xl px-4 py-3 text-sm border border-white/5 focus:outline-none focus:border-sky-500/50 placeholder:text-slate-600 resize-none"
          />
          <p className="text-slate-600 text-xs mt-1 text-right">{body.length}/200</p>
        </div>

        {/* 이동 URL (선택) */}
        <div>
          <label className="block text-slate-400 text-sm font-medium mb-2">
            <Link className="inline w-3.5 h-3.5 mr-1" />
            탭 시 이동 URL <span className="text-slate-600">(선택)</span>
          </label>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://urpink.co.kr/playlists"
            className="w-full bg-[#0d1526] text-white rounded-xl px-4 py-3 text-sm border border-white/5 focus:outline-none focus:border-sky-500/50 placeholder:text-slate-600"
          />
        </div>

        {/* 미리보기 */}
        {(title || body) && (
          <div className="bg-[#0d1526] rounded-xl p-4 border border-white/5">
            <p className="text-slate-500 text-xs mb-3 uppercase tracking-widest">알림 미리보기</p>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-sky-600 flex items-center justify-center shrink-0">
                <Bell className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-semibold truncate">{title || '제목'}</p>
                <p className="text-slate-400 text-xs mt-0.5 line-clamp-2">{body || '내용'}</p>
              </div>
            </div>
          </div>
        )}

        {/* 발송 결과 */}
        {result && (
          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4">
            <p className="text-emerald-400 font-semibold text-sm mb-1">✓ 발송 완료</p>
            <p className="text-slate-400 text-xs">
              성공 {result.sent?.toLocaleString() ?? 0}건
              {result.failed > 0 && ` / 실패 ${result.failed}건 (무효 토큰 자동 삭제)`}
            </p>
          </div>
        )}

        {/* 발송 버튼 */}
        <button
          onClick={handleSend}
          disabled={sending || !title || !body}
          className="w-full flex items-center justify-center gap-2 bg-sky-600 hover:bg-sky-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold rounded-xl py-3.5 transition-colors">
          {sending ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              발송 중...
            </>
          ) : (
            <>
              <Send className="w-4 h-4" />
              푸시 알림 발송
            </>
          )}
        </button>
      </div>
    </div>
  );
}
